import { type LanguageConfig, languages } from '@motimate/ui/utils/language';
import { emailRgx } from '@motimate/utils';
import { Dialog } from '@ui/components/dialog';
import { Cross } from '@ui/components/icon';
import { sendToast } from '@ui/components/toast';
import { Button } from '@ui/components/v2/button';
import { LanguageSelect } from '@ui/components/v2/combobox/languages';
import { Input } from '@ui/components/v2/form/input';
import { Label } from '@ui/components/v2/form/label';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import type { Page } from '@/types';
import { MOTIMATE_URL } from '../../config';
import { SearchSelect } from './SearchSelect';

export const Form: FC<{ page: Page; lang: string }> = ({ page, lang }) => {
  const [isSubmitDialog, setIsSubmitDialog] = useState(false);
  const [isSuccessDialog, setIsSuccessDialog] = useState(false);

  const getInitialGroups = () => {
    const group = page.groups.find(g => g.motimateId === String(page.defaultGroupId || ''));
    if (group) return [group];
    if (page.groups.length > 1) return [];
    if (!page.requireGroupSelect) return [];
    return page.groups;
  };

  const getInitialPositions = () => {
    const position = page.positions.find(p => p.motimateId === String(page.defaultPositionId || ''));
    if (position) return [position];
    if (page.positions.length > 1) return [];
    if (!page.requirePositionSelect) return [];
    return page.positions;
  };

  const t = useTranslations('general');
  const tCommon = useTranslations('common');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState({ value: '', error: '' });
  const [language, setLanguage] = useState(lang as keyof LanguageConfig);
  const [freeTextField, setFreeTextField] = useState('');
  const [phoneNumber, setPhoneNumber] = useState({ value: '', error: '' });
  const [groups, setGroups] = useState(getInitialGroups);
  const [positions, setPositions] = useState(getInitialPositions);

  const onlyDefaultGroup = page.groups.length === 1 && page.groups[0].motimateId === String(page.defaultGroupId || '');
  const onlyDefaultPosition =
    page.positions.length === 1 && page.positions[0].motimateId === String(page.defaultPositionId || '');
  return (
    <div className="text-left flex flex-col gap-2">
      <div className="flex flex-col">
        <Label variant="regular">{t('inputFirstName')}</Label>
        <Input placeholder="Joe" value={firstName} onChange={e => setFirstName(e.currentTarget.value)} />
      </div>
      <div className="flex flex-col">
        <Label variant="regular">{t('inputLastName')}</Label>
        <Input placeholder="Public" value={lastName} onChange={e => setLastName(e.currentTarget.value)} />
      </div>
      <div className="transition-all flex flex-col">
        <Label variant="regular">{t('email')}</Label>
        <Input
          variant={email.error ? 'error' : 'default'}
          placeholder="sample@domain.com"
          value={email.value}
          onChange={e => setEmail({ value: e.currentTarget.value, error: '' })}
        />
        {email.error && (
          <span className="pt-1 pl-0 h-full flex items-center animate-fade-in text-status-danger-bg text-xs font-bold">
            {email.error}
          </span>
        )}
      </div>
      {page.enablePhoneNumber && (
        <div className="relative">
          <span className="text-sm leading-ui -mt-0.5 mb-0.5 block">{t('phoneNumber')}</span>
          <PhoneInput
            containerClass={`rounded-lg [&>ul]:w-[400px]! [&>ul]:!rounded-lg ${
              phoneNumber.error
                ? `border-status-danger-bg border-2 mb-1`
                : `border hover:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500`
            }`}
            country="no"
            inputStyle={{ width: '100%', padding: '18px 50px', border: 'none', borderRadius: '0.5rem' }}
            buttonStyle={{
              background: 'transparent',
              border: 'none',
              borderRadius: '.5rem 0 0 .5rem',
              borderRight: '1px solid rgb(209 213 219 / 0.5)',
            }}
            inputProps={{
              id: 'phoneNumber',
            }}
            jumpCursorToEnd={false}
            value={phoneNumber.value}
            onChange={value => setPhoneNumber({ value, error: '' })}
          />
          {phoneNumber.error && (
            <div className="text-status-danger-bg text-xs font-bold ease-in-out duration-200 animate-fade-in">
              {phoneNumber.error}
            </div>
          )}
        </div>
      )}
      {page.freeTextFieldName && (
        <div className="flex flex-col">
          <Label variant="regular">{page.freeTextFieldName}</Label>
          <Input
            placeholder={page.freeTextFieldName}
            value={freeTextField}
            onChange={e => setFreeTextField(e.currentTarget.value)}
          />
        </div>
      )}

      <div className="flex flex-col items-start w-full relative">
        <span className={`block mb-1 text-gray-900 text-sm leading-ui font-normal`}>{t('language')}</span>

        <LanguageSelect containerClassName="w-full" value={language} onChange={setLanguage} />
      </div>

      {Boolean(page.groups.length) &&
        !onlyDefaultGroup &&
        (page.selectMultipleGroups ? (
          <>
            <SearchSelect
              label={page.customGroupLabel || t('groups')}
              placeholder={t('inputGroupsPlaceholder', { value: page.customGroupLabel || t('group') })}
              labelContainerClassName="!font-normal !mb-1"
              options={page.groups.map(g => ({
                label: g.label,
                value: g.motimateId,
                isDisabled: groups.some(g2 => g2.id === g.id),
              }))}
              onChange={v => setGroups(g => [...g, page.groups.find(g2 => g2.motimateId === v)!])}
            />
            <div className="w-full [min-height: 50px] flex flex-wrap gap-1 p-2">
              {groups.map(g => (
                <div
                  key={g.id}
                  className="items-center relative px-3 inline-flex text-xs font-medium rounded-2xl h-6 text-slate-800 bg-slate-100 animate-fade-in"
                >
                  {g.label.slice(0, 50)}
                  {g.label.length > 50 && '…'}
                  {g.motimateId !== String(page.defaultGroupId || '') && (
                    <Cross
                      onClick={() => setGroups(p => p.filter(g2 => g2.motimateId !== g.motimateId))}
                      className="ml-2 w-2 cursor-pointer"
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <SearchSelect
            value={groups[0]?.motimateId}
            disabled={page.groups.length === 1}
            label={page.customGroupLabel || t('group')}
            placeholder={t('inputGroupsPlaceholder', { value: page.customGroupLabel || t('group') })}
            labelContainerClassName="!font-normal !mb-1"
            options={page.groups.map(g => ({ label: g.label, value: g.motimateId }))}
            onChange={g => {
              const group = page.groups.find(g2 => g2.motimateId === g);
              if (group) setGroups([group]);
            }}
          />
        ))}
      {Boolean(page.positions.length) &&
        !onlyDefaultPosition &&
        (page.selectMultiplePositions ? (
          <>
            <SearchSelect
              label={page.customPositionLabel || t('positions')}
              placeholder={t('inputPositionsPlaceholder', { value: page.customPositionLabel || t('position') })}
              labelContainerClassName="!font-normal !mb-1"
              options={page.positions.map(p => ({
                label: p.label,
                value: p.motimateId,
                isDisabled: positions.some(p2 => p2.id === p.id),
              }))}
              onChange={v => setPositions(p => [...p, page.positions.find(p2 => p2.motimateId === v)!])}
            />

            <div className="w-full [min-height: 50px] flex flex-wrap gap-1 p-2">
              {positions.map(p => (
                <div
                  key={p.id}
                  className="items-center relative px-3 inline-flex text-xs font-medium rounded-2xl h-6 text-slate-800 bg-slate-100 animate-fade-in"
                >
                  {p.label.slice(0, 50)}
                  {p.label.length > 50 && '…'}
                  {p.motimateId !== String(page.defaultPositionId || '') && (
                    <Cross
                      onClick={() => setPositions(prev => prev.filter(p2 => p2.motimateId !== p.motimateId))}
                      className="ml-2 w-2 cursor-pointer"
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <SearchSelect
            value={positions[0]?.motimateId}
            disabled={page.positions.length === 1}
            label={page.customPositionLabel || t('position')}
            placeholder={t('inputPositionsPlaceholder', { value: page.customPositionLabel || t('position') })}
            labelContainerClassName="!font-normal !mb-1"
            options={page.positions.map(p => ({ label: p.label, value: p.motimateId }))}
            onChange={g => {
              const position = page.positions.find(g2 => g2.motimateId === g);
              if (position) setPositions([position]);
            }}
          />
        ))}
      <Button
        variant="dark"
        onClick={() => {
          const isEmailValid = email.value.match(emailRgx);

          if (!isEmailValid) setEmail(p => ({ ...p, error: t('invalidEmail') }));

          const isPhoneNumberValid = !page.enablePhoneNumber || phoneNumber.value.match(/^\d{7,15}$/);
          if (!isPhoneNumberValid) setPhoneNumber(p => ({ ...p, error: t('invalidPhoneNumber') }));

          if (isPhoneNumberValid && isEmailValid) setIsSubmitDialog(true);
        }}
        disabled={
          !firstName ||
          !lastName ||
          !email ||
          Boolean(!groups.length && page.groups.length && page.requireGroupSelect) ||
          Boolean(!positions.length && page.positions.length && page.requirePositionSelect) ||
          Boolean(page.freeTextFieldName && !freeTextField) ||
          (page.enablePhoneNumber && !phoneNumber)
        }
        className="mt-2 h-10 px-6 text-sm leading-none rounded-xl"
      >
        {t('createUser')}
      </Button>
      <Dialog
        title={t('createUserPopupTitle')}
        open={isSubmitDialog}
        onClose={() => setIsSubmitDialog(false)}
        actionText={tCommon('confirm')}
        cancelText={tCommon('cancel')}
        onAction={async () => {
          try {
            const res = await fetch('/api/users', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                pageSlug: page.slug,
                email: email.value,
                firstName,
                lastName,
                phoneNumber: phoneNumber.value,
                freeTextFieldName: freeTextField,
                language,
                groups: groups.map(g => Number(g.motimateId)),
                positions: positions.map(p => Number(p.motimateId)),
              }),
            });

            if (!res.ok) {
              const json = await res.json().catch(() => null);
              throw Error(json?.error || res.statusText);
            }
          } catch (err) {
            console.error(err);
            sendToast.error(t('createUserError'));
            return;
          }
          sendToast.success(t('createUserSuccess'));

          setIsSubmitDialog(false);
          setIsSuccessDialog(true);
          setFirstName('');
          setLastName('');
          setEmail({ error: '', value: '' });
          setLanguage('en');
          setFreeTextField('');
          setPhoneNumber({ error: '', value: '' });
          setGroups(getInitialGroups());
          setPositions(getInitialPositions());
        }}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col">
            <span className="text-slate-500 text-sm">{t('inputFirstName')}</span>
            <span className="text-slate-800 font-semibold border-b pb-2">{firstName}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-slate-500 text-sm">{t('inputLastName')}</span>
            <span className="text-slate-800 font-semibold border-b pb-2">{lastName}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-slate-500 text-sm">Email</span>
            <span className="text-slate-800 font-semibold border-b pb-2">{email.value}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-slate-500 text-sm">{t('language')}</span>
            <span className="text-slate-800 font-semibold border-b pb-2">{languages[language].name}</span>
          </div>
          {page.enablePhoneNumber && (
            <div className="flex flex-col">
              <span className="text-slate-500 text-sm">{t('phoneNumber')}</span>
              <span className="text-slate-800 font-semibold border-b pb-2">+{phoneNumber.value}</span>
            </div>
          )}
          {Boolean(groups.length) && (
            <div className="flex flex-col">
              <span className="text-slate-500 text-sm">{page.customGroupLabel || t('groups')}</span>
              <div className="text-slate-800 font-semibold border-b pb-2 flex flex-wrap mt-1">
                {groups.map(g => (
                  <div
                    key={g.id}
                    className="items-center relative px-3 inline-flex text-xs font-medium rounded-2xl h-6 text-slate-800 bg-slate-100"
                  >
                    {g.label.slice(0, 50)}
                    {g.label.length > 50 && '…'}
                  </div>
                ))}
              </div>
            </div>
          )}
          {Boolean(positions.length) && (
            <div className="flex flex-col">
              <span className="text-slate-500 text-sm">{page.customPositionLabel || t('positions')}</span>
              <div className="text-slate-800 font-semibold border-b pb-2 flex flex-wrap mt-1">
                {positions.map(p => (
                  <div
                    key={p.id}
                    className="items-center relative px-3 inline-flex text-xs font-medium rounded-2xl h-6 text-slate-800 bg-slate-100"
                  >
                    {p.label.slice(0, 50)}
                    {p.label.length > 50 && '…'}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Dialog>
      <Dialog
        open={isSuccessDialog}
        title={t('createUserSuccess')}
        actionText={t('loginAction')}
        cancelText={t('createNew')}
        onAction={() => {
          window.location.href = `${MOTIMATE_URL}/${page.organizationSlug}/signin`;
        }}
        onClose={() => setIsSuccessDialog(false)}
      >
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" className="w-40 mx-auto">
          <path
            fill="#1e293b"
            opacity="0.1"
            d="M819.204 856.441c0 15.424-125.036 27.928-279.277 27.928s-279.277-12.504-279.277-27.928c0-15.424 125.036-27.928 279.277-27.928s279.277 12.504 279.277 27.928z"
          />
          <path
            fill="#10b981"
            opacity="0.4"
            d="M178.636 463.34l3.352 20.415 0.016 0.114c1.082 6.402 5.298 11.739 11.136 14.427l18.972 8.577c6.665 3.073 11.29 9.814 11.29 17.635 0 4.246-1.364 8.174-3.638 11.313l-12.025 16.755c-2.311 3.195-3.673 7.12-3.673 11.364 0 2.464 0.459 4.819 1.251 6.855l7.298 19.364-0.045-0.133c0.837 2.169 1.296 4.525 1.296 6.989 0 9.61-6.984 17.589-16.267 19.153l-20.529 3.367c-6.402 1.082-11.739 5.298-14.427 11.136l-8.527 18.861-0.050 0.12c-3.075 6.661-9.814 11.284-17.632 11.284-4.248 0-8.177-1.364-11.316-3.64l-16.756-12.017c-3.196-2.314-7.125-3.679-11.372-3.679-2.46 0-4.813 0.457-6.846 1.248l-19.231 7.253c-2.169 0.837-4.525 1.296-6.989 1.296-9.61 0-17.589-6.984-19.153-16.267l-3.352-20.415-0.016-0.114c-1.082-6.402-5.298-11.739-11.136-14.427l-18.981-8.577c-6.661-3.075-11.284-9.814-11.284-17.632 0-4.248 1.364-8.177 3.64-11.316l12.064-16.812c3.779-5.278 4.682-12.093 2.383-18.163l-7.298-19.364 0.045 0.133c-0.837-2.169-1.296-4.525-1.296-6.989 0-9.61 6.984-17.589 16.267-19.153l20.529-3.367c6.402-1.082 11.739-5.298 14.427-11.136l8.527-18.861 0.050-0.12c3.075-6.661 9.814-11.284 17.632-11.284 4.248 0 8.177 1.364 11.316 3.64l16.756 12.017c3.196 2.314 7.125 3.679 11.372 3.679 2.46 0 4.813-0.457 6.846-1.248l19.231-7.253c2.169-0.837 4.525-1.296 6.989-1.296 9.61 0 17.589 6.984 19.153 16.267zM80.489 576.010c2.681 3.474 6.845 5.689 11.527 5.689 2.076 0 4.049-0.435 5.835-1.22l-0.093 0.037 59.216-25.293c2.623-1.142 4.424-3.712 4.424-6.702 0-1.037-0.217-2.022-0.606-2.915l0.018 0.047c-1.158-2.607-3.724-4.392-6.707-4.392-1.027 0-2.005 0.212-2.892 0.593l0.048-0.018-59.216 25.293-8.49-31.661c-0.511-1.904-1.732-3.461-3.354-4.403l-0.034-0.019c-1.058-0.64-2.335-1.019-3.701-1.019-3.995 0-7.233 3.238-7.233 7.233 0 0.704 0.101 1.385 0.288 2.029l-0.013-0.051 8.499 31.688c0.484 1.844 1.331 3.565 2.485 5.083zM891.955 626.745l17.709 11.123c3.053 1.941 6.676 3.065 10.562 3.065 2.927 0 5.706-0.638 8.081-1.732l19.036-8.551c2.498-1.145 5.276-1.783 8.204-1.783 9.33 0 17.146 6.484 19.214 15.325l4.682 20.48 0.027 0.133c1.488 6.358 6.056 11.53 12.178 13.905l19.777 7.512c7.359 2.858 12.575 10.008 12.575 18.378 0 3.886-1.124 7.509-3.019 10.482l-11.132 17.71c-1.94 3.051-3.063 6.673-3.063 10.557 0 2.926 0.637 5.703 1.731 8.076l8.602 19.177-0.051-0.123c1.142 2.496 1.779 5.271 1.779 8.195 0 9.329-6.481 17.143-15.321 19.213l-20.614 4.709c-6.355 1.493-11.526 6.058-13.905 12.178l-7.456 19.643-0.046 0.134c-2.863 7.355-10.013 12.568-18.381 12.568-3.888 0-7.513-1.125-10.487-3.020l-17.71-11.123c-3.051-1.94-6.673-3.063-10.557-3.063-2.926 0-5.703 0.637-8.076 1.731l-19.054 8.551c-2.496 1.142-5.271 1.779-8.195 1.779-9.329 0-17.143-6.481-19.213-15.321l-4.682-20.48-0.027-0.134c-1.489-6.357-6.057-11.529-12.178-13.905l-19.777-7.512c-7.355-2.86-12.568-10.008-12.568-18.375 0-3.887 1.125-7.511 3.020-10.484l11.171-17.79c3.51-5.585 3.984-12.549 1.285-18.554l-8.602-19.177 0.051 0.123c-1.142-2.496-1.779-5.271-1.779-8.195 0-9.329 6.481-17.143 15.321-19.213l20.614-4.709c6.359-1.492 11.531-6.062 13.905-12.187l7.466-19.643 0.046-0.134c2.86-7.355 10.008-12.568 18.375-12.568 3.887 0 7.511 1.125 10.484 3.020zM911.542 776.363c0.308 0.023 0.668 0.036 1.030 0.036 6.497 0 12.014-4.2 13.983-10.032l0.030-0.104 20.582-62.065c0.236-0.693 0.373-1.491 0.373-2.32 0-3.26-2.105-6.029-5.031-7.020l-0.052-0.016c-0.685-0.23-1.473-0.363-2.292-0.363-3.255 0-6.020 2.096-7.020 5.012l-0.016 0.052-20.573 62.065-29.808-14.839c-0.956-0.49-2.087-0.778-3.284-0.778-0.84 0-1.648 0.142-2.4 0.403l0.052-0.016c-2.98 0.984-5.094 3.744-5.094 6.998 0 2.897 1.675 5.403 4.11 6.602l0.043 0.020 29.827 14.839c1.732 0.875 3.612 1.396 5.539 1.527zM843.333 168.005l1.117 16.906 0.002 0.049c0.368 5.402 3.435 10.062 7.938 12.686l14.69 8.444c8.695 4.999 10.65 16.71 4.040 24.26l-11.155 12.736c-2.46 2.8-3.953 6.472-3.953 10.492 0 1.473 0.2 2.899 0.549 4.141l4.422 16.357-0.027-0.112c0.376 1.356 0.578 2.785 0.578 4.261 0 8.412-6.532 15.298-14.85 15.866l-16.955 1.12c-5.402 0.367-10.064 3.434-12.686 7.938l-8.444 14.69c-4.999 8.695-16.71 10.65-24.26 4.040l-12.736-11.155c-2.799-2.461-6.47-3.954-10.49-3.954-1.474 0-2.901 0.201-4.144 0.55l-16.245 4.395c-1.356 0.376-2.785 0.578-4.261 0.578-8.412 0-15.298-6.532-15.866-14.85l-1.117-16.906c-0.345-5.306-3.333-10.091-7.941-12.735l-14.69-8.444c-8.695-4.999-10.65-16.71-4.040-24.26l11.171-12.754c3.5-3.994 4.766-9.477 3.388-14.616l-4.422-16.357 0.027 0.112c-0.376-1.356-0.578-2.785-0.578-4.261 0-8.412 6.532-15.298 14.85-15.866l16.955-1.12c5.402-0.368 10.062-3.435 12.686-7.938l8.444-14.69c4.999-8.695 16.71-10.65 24.26-4.040l12.736 11.155c2.8 2.46 6.472 3.953 10.492 3.953 1.473 0 2.899-0.2 4.141-0.549l16.245-4.395c1.353-0.374 2.779-0.575 4.252-0.575 8.411 0 15.296 6.531 15.865 14.848zM755.649 249.462c2.138 3.388 5.861 5.606 10.103 5.606 1.289 0 2.531-0.205 3.694-0.584l-0.084 0.023 50.279-15.938c2.433-0.794 4.159-3.042 4.159-5.693 0-0.643-0.102-1.262-0.289-1.843l0.012 0.042c-0.806-2.424-3.053-4.141-5.701-4.141-0.633 0-1.244 0.099-1.816 0.28l0.042-0.012-50.279 15.947-4.413-26.484c-0.268-1.608-1.155-2.971-2.403-3.861l-0.018-0.013c-0.966-0.709-2.177-1.134-3.489-1.134-3.275 0-5.93 2.655-5.93 5.93 0 0.365 0.033 0.722 0.096 1.069l-0.005-0.036 4.413 26.513c0.251 1.536 0.81 3.007 1.629 4.338z"
          />
          <path
            fill="#10b981"
            d="M539.927 214.105c164.513 0 297.895 133.382 297.895 297.895s-133.354 297.895-297.895 297.895c-0.141 0-0.307 0-0.474 0-59.746 0-115.365-17.667-161.918-48.063l1.138 0.697-62.195 18.311c-18.814 5.539-36.306-11.953-30.758-30.767l17.017-57.81c-37.88-49.287-60.706-111.862-60.706-179.768 0-0.174 0-0.348 0.001-0.522v0.027c0-164.513 133.354-297.895 297.895-297.895zM540.559 298.167c-133.243 0-241.239 74.353-241.267 207.409 0 126.429 97.57 130.050 221.634 130.096h39.257c124.045-0.047 221.615-3.668 221.588-130.096 0-133.029-108.015-207.391-241.212-207.409zM543.585 338.383c110.026 0 199.217 57.717 199.217 160.975 0 98.082-80.497 100.93-182.88 100.968h-32.675c-102.383-0.038-182.88-2.886-182.88-100.968 0-103.248 89.183-160.975 199.217-160.975zM461.338 443.168c-0.003 0-0.006 0-0.009 0-25.556 0-46.275 20.712-46.285 46.266v0.001c0 25.572 20.732-1.508 46.294-1.489 25.563 0 46.313 27.062 46.294 1.489 0-0.003 0-0.006 0-0.009 0-25.547-20.71-46.258-46.258-46.258-0.013 0-0.027 0-0.039 0h0.002zM625.804 443.168c-25.556 0-46.275 20.712-46.285 46.266v0.001c0 25.572 20.722-1.508 46.285-1.489 25.572 0 46.323 27.062 46.323 1.489 0-25.553-20.715-46.267-46.267-46.267-0.020 0-0.039 0-0.059 0h0.003z"
          />
        </svg>

        {page.pageType === 'SELF_SIGNUP_APPROVAL' ? t('createUserInstructions') : t('createUserInstructionsOpen')}
      </Dialog>
    </div>
  );
};
